// Auto-generated by splitter.py — do not regenerate; edit directly.

import React, { useState, useEffect } from 'react';
import { Activity, AlertCircle, AlertTriangle, Banknote, Bell, Calendar, CheckCircle2, ChevronDown, ClipboardList, Computer, Database, Filter, HeartHandshake, Key, Loader2, Power, ScrollText, Search, ShieldCheck, Star, User, X } from 'lucide-react';
import { QuickActionsPopover } from '../QuickActionsPopover';
import { resolveActivityTargetPage } from '../../lib/activityRouting';
import { getBackendUrl } from '../../lib/backendUrl';
import { BRAND } from '../../lib/brand';
import { toast } from '../../lib/buses';
import { BiometricPanel } from '../BiometricPanel';
import { Button } from '../../ui/Button';
import { useBackendHealth, HEALTH_TIERS, formatUptime } from '../../lib/useBackendHealth';

const fmtCount = (value) => Number(value || 0).toLocaleString('en-UG');
/** Initials for the avatar. Falls back to the first letter of an email when there is no name. */
const initialsOf = (name) => String(name || '?')
  .replace(/@.*$/, '')
  .split(/[\s._-]+/).filter(Boolean).slice(0, 2)
  .map((p) => p[0]?.toUpperCase() || '').join('') || '?';
const fmtMoneyShort = (value) => {
  const amount = Number(value || 0);
  if (!amount) return 'UGX 0';
  if (amount >= 1_000_000) return `UGX ${(amount / 1_000_000).toFixed(1)}M`;
  return `UGX ${amount.toLocaleString('en-UG')}`;
};

export const TopBar = ({ onOpenPalette, onQuickAction, onNavigate, backendStatus, dashboard, sessionUser = null, onSignOut }) => {
  const operatorName = sessionUser?.name || sessionUser?.email || 'Operator';
  const status = backendStatus || { state: 'loading', label: 'Checking backend', detail: 'Backend not checked' };
  const statusTone = status.state === 'live' ? 'green' : (status.state === 'loading' || status.state === 'reachable') ? 'amber' : 'neutral';
  const StatusIcon = status.state === 'loading' ? Loader2 : Database;
  const navigateTo = (page) => { if (page && onNavigate) onNavigate(page); };
  const pendingRequests = dashboard?.attention?.filter((item) => item.target === 'event-requests').length || 0;
  const failedPayments = dashboard?.attention?.filter((item) => item.target === 'orders' && item.severity === 'red').length || 0;
  const pendingPayouts = dashboard?.kpi?.pendingPayouts || 0;
  const savedViews = dashboard ? [
    { icon: ClipboardList, label: 'Pending event requests', sub: 'Event requests · status needs review', meta: `${fmtCount(pendingRequests)} shown` },
    { icon: AlertCircle, label: 'Failed loaded payments', sub: 'Orders · failed or declined in loaded window', meta: fmtCount(failedPayments) },
    { icon: Banknote, label: 'Payouts awaiting action', sub: 'Payouts · pending, held, or processing', meta: fmtCount(pendingPayouts) },
    { icon: Calendar, label: 'Active events', sub: 'Events · live or upcoming', meta: fmtCount(dashboard.activeEvents?.length) },
  ] : [];
  const alertItems = (dashboard?.attention || []).slice(0, 5).map((item) => ({
    icon: item.target === 'payouts' ? Banknote : item.target === 'orders' ? AlertCircle : ClipboardList,
    label: item.label,
    sub: item.meta,
    meta: item.time,
    tone: item.severity === 'red' ? 'red' : 'amber',
    onClick: () => navigateTo(item.target),
  }));
  const notificationItems = (dashboard?.recentActivity || []).slice(0, 5).map((item) => ({
    icon: ScrollText,
    label: item.action,
    sub: `${item.actor} · ${item.target}`,
    meta: item.time,
    onClick: () => navigateTo(resolveActivityTargetPage(item)),
  }));
  const alertSubtitle = dashboard
    ? `${alertItems.length} item${alertItems.length === 1 ? '' : 's'} need attention`
    : 'Dashboard data is still loading';
  const notificationSubtitle = dashboard
    ? `${notificationItems.length} recent activity entr${notificationItems.length === 1 ? 'y' : 'ies'}`
    : 'Activity loads after sign-in';

  // ── Backend health ────────────────────────────────────────────────────────────────────────
  // A measured poll, not the shell's session-derived `backendStatus` — see useBackendHealth for
  // why that one cannot move. `status` is still used for the session line below.
  const health = useBackendHealth(true);
  const tier = HEALTH_TIERS[health.tier] || HEALTH_TIERS.unknown;
  const h = health.data || {};
  const gapCount = Object.keys(dashboard?.source?.errors || {}).length;
  const healthSubtitle = health.tier === 'down'
    ? (health.error || 'Not responding')
    : `${tier.label}${health.latencyMs != null ? ` · ${health.latencyMs}ms` : ''}`;

  // Only rows the response actually supports. A local backend returns no DB_ENV or commit, and a
  // row reading "—" for something that was never sent is noise pretending to be a reading.
  const healthItems = [
    {
      icon: Activity,
      label: 'Response time',
      sub: health.checkedAt ? `Checked ${new Date(health.checkedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}` : 'Checking…',
      meta: health.latencyMs != null ? `${health.latencyMs}ms` : '—',
      tone: health.tier === 'down' ? 'red' : health.tier === 'slow' ? 'amber' : undefined,
    },
    ...(h.uptimeSec ? [{
      icon: Power,
      label: 'Uptime',
      // A backend that restarted minutes ago explains a great deal of "it worked a second ago".
      sub: h.startedAt ? `Started ${new Date(h.startedAt).toLocaleString('en-GB')}` : 'Since last restart',
      meta: formatUptime(h.uptimeSec),
    }] : []),
    ...(h.DB_ENV ? [{
      icon: Database,
      label: 'Database',
      // THE FIELD WORTH THE PANEL. Which database this backend is pointed at is invisible
      // everywhere else in the console, and the difference between local and a live customer
      // database is the difference between a safe experiment and a real refund.
      sub: h.DB_ENV === 'local' ? 'Local database — safe to experiment' : 'Live data — changes here are real',
      meta: h.DB_ENV,
      tone: h.DB_ENV === 'local' ? undefined : 'red',
    }] : []),
    ...(h.commit ? [{
      icon: ShieldCheck,
      label: 'Build',
      sub: 'The commit actually running, which a clean tree cannot tell you',
      meta: String(h.commit).slice(0, 7),
    }] : []),
    ...(h.CLUSTER_ID ? [{
      icon: Database,
      label: 'Instance',
      sub: h.cpus ? `${h.cpus} CPUs` : 'Cluster member',
      meta: h.TOTAL_CLUSTERS ? `${h.CLUSTER_ID}/${h.TOTAL_CLUSTERS}` : String(h.CLUSTER_ID),
    }] : []),
    // NO "DATA GAPS" ROW. It read off `dashboardChrome`, which starts null and is only filled when
    // DashboardPage loads — so the row appeared for anyone who had visited the Dashboard this
    // session and was silently missing for everyone else. A global status panel that changes shape
    // depending on where you happen to have been is worse than one that omits the fact: you cannot
    // tell "no gaps" from "not measured". The Dashboard's own System health card owns it, with the
    // data actually in scope there.
    {
      icon: StatusIcon,
      label: status.state === 'live' ? 'Session active' : 'No session',
      sub: getBackendUrl(),
      meta: status.state,
    },
  ];

  return (
    <header className="h-14 bg-white border-b border-[#E6E4E1] sticky top-0 z-30 flex items-center px-6 gap-4">
      {/* Global search — opens command palette */}
      <button onClick={onOpenPalette} className="flex-1 max-w-[480px] relative text-left" type="button">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" strokeWidth={1.75} />
        <div className="w-full h-9 bg-[#F7F6F2] border border-[#E6E4E1] rounded-md pl-9 pr-16 text-[12.5px] text-[#9CA3AF] flex items-center hover:bg-white hover:border-[#D8D5CF]">
          Search
        </div>
        <kbd className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-[#9CA3AF] bg-white border border-[#E6E4E1] rounded px-1.5 py-0.5 font-mono">⌘K</kbd>
      </button>

      <div className="flex-1" />

      <QuickActionsPopover onAction={onQuickAction} />

      <div className="w-px h-6 bg-[#E6E4E1] mx-1" />

      {/* Top right icons */}
      <div className="flex items-center gap-1">
        {/* Backend status — icon, plus a BEACON under it.
            The corner dot is gone: a dot has two states an operator can distinguish (there / not
            there), and it sat in the same corner as the alert count, which made two unrelated
            things look like the same kind of signal. The beacon is a small track under the icon
            that fills and colours by measured health — green, amber, red — so a degrading backend
            reads as degrading instead of flipping between fine and broken.
            The FILL is a coarse tier, not a percentage; see HEALTH_TIERS. */}
        <TopBarPopover
          align="right"
          trigger={({ open }) => (
            <span
              role="button"
              tabIndex={0}
              aria-haspopup="dialog"
              aria-expanded={open}
              className="flex flex-col items-center justify-center w-9 h-9 rounded-md hover:bg-[#F2F1ED] cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D8D5CF]"
              title={`Backend · ${tier.label}${health.latencyMs != null ? ` · ${health.latencyMs}ms` : ''}`}
            >
              <Computer
                className={`w-[17px] h-[17px] ${health.tier === 'down' ? 'text-[#C53030]' : 'text-[#4B5563]'}`}
                strokeWidth={1.75}
              />
              {/* Track and fill, both rounded on each end, so a partial fill still reads as a
                  lozenge rather than a bar someone clipped. */}
              <span className="mt-[3px] w-4 h-[3px] rounded-full bg-[#EEEDEA] overflow-hidden">
                <span
                  className={`block h-full rounded-full transition-all duration-500 ${health.tier === 'unknown' ? 'animate-pulse' : ''}`}
                  style={{ width: tier.fill, backgroundColor: tier.color }}
                />
              </span>
            </span>
          )}
          title="Backend"
          subtitle={healthSubtitle}
          items={healthItems}
        />
        <TopBarPopover
          icon={<AlertTriangle className="w-[17px] h-[17px] text-[#C53030]" strokeWidth={1.75} />}
          dot={alertItems.some((item) => item.tone === 'red') ? 'red' : alertItems.length ? 'amber' : undefined}
          badge={alertItems.length}
          title="Active alerts"
          subtitle={alertSubtitle}
          empty="No pending requests, payout items, or failed payments in the loaded window."
          items={alertItems}
          onViewAll={() => navigateTo('dashboard')}
        />
        <TopBarPopover
          icon={<Bell className="w-[17px] h-[17px] text-[#4B5563]" strokeWidth={1.75} />}
          dot={notificationItems.length ? 'green' : undefined}
          badge={notificationItems.length}
          title="Notifications"
          subtitle={notificationSubtitle}
          empty="No activity entries returned."
          items={notificationItems}
          onViewAll={() => navigateTo('audit')}
        />
      </div>

      <div className="w-px h-6 bg-[#E6E4E1] mx-1" />

      {/* Profile.
          Built from header/footer/children rather than `items`, because the three things in here
          are three different KINDS of thing and the flat item list rendered them identically:
          the person (a heading), their role (a fact), enrolled devices (a form), and signing out
          (a destructive act) all arrived as look-alike clickable rows. */}
      <TopBarPopover
        align="right"
        trigger={
          /* THE GREEN DOT IS GONE. It was `bg-[#2F8B4F]`, hardcoded — bound to nothing, so it was
             green whether the backend was healthy, degraded or on fire. A status light that cannot
             change is the defect just removed from the backend indicator, and this one sat two
             controls away from the real beacon, where it read as a second opinion about the same
             thing. (There is no honest signal it could carry: if you can see this menu you have a
             session, so it would be green forever anyway.)

             Its place is taken by the operator's initials — identity, which is what this control is
             actually for, and the same avatar the panel it opens leads with.

             Outlined to match Quick actions: the two controls that OPEN things now bracket the row
             of icon buttons, in the console's own `secondary`, so neither is a bare label floating
             in the header. */
          ({ open }) => (
          <span className="cursor-pointer">
            <Button variant="secondary" size="md" aria-haspopup="menu" aria-expanded={open}>
              <span className="w-5 h-5 rounded-full bg-[#F2F1ED] text-[#4B5563] text-[9.5px] font-semibold flex items-center justify-center flex-shrink-0">
                {initialsOf(operatorName)}
              </span>
              <span className="max-w-[140px] truncate">{operatorName}</span>
              <ChevronDown className={`w-3.5 h-3.5 text-[#9CA3AF] flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} strokeWidth={1.75} />
            </Button>
          </span>
          )
        }
        header={
          // The person, not the abstraction. It said "Operator session" over an email — a heading
          // that described the MENU rather than naming who is signed in, while the name itself
          // appeared only on the trigger that had just been covered by this panel.
          <div className="px-4 py-3.5 border-b border-[#EEEDEA] flex items-center gap-3">
            <span className="w-9 h-9 rounded-full bg-[#F2F1ED] text-[#4B5563] text-[12.5px] font-semibold flex items-center justify-center flex-shrink-0">
              {initialsOf(operatorName)}
            </span>
            <span className="min-w-0">
              <span className="block text-[13px] font-semibold text-[#1F2937] truncate">{operatorName}</span>
              <span className="block text-[11px] text-[#6B7280] truncate">{sessionUser?.email || `${BRAND.name} Back Office`}</span>
            </span>
          </div>
        }
        footer={
          // A FOOTER, not the last row of a list. Sign out is the only irreversible thing in this
          // menu and it looked exactly like "Role" directly above it — same tile, same size, same
          // hover. Full width and boxed so it reads as a control, separated so it cannot be hit by
          // someone aiming at the row above.
          <div className="px-4 py-3 border-t border-[#EEEDEA] bg-[#FBFAF7]">
            <Button variant="danger" size="md" icon={Power} className="w-full" onClick={() => onSignOut && onSignOut()}>
              Sign out
            </Button>
          </div>
        }
      >
        {/* A fact, so it is rendered as one: label left, value right, no tile and no hover — it was
            a button that did nothing but toast its own label back. */}
        <div className="px-4 py-2.5 border-b border-[#EEEDEA] flex items-center justify-between gap-3">
          <span className="text-[10px] uppercase tracking-[0.06em] font-semibold text-[#9CA3AF]">Role</span>
          <span className="text-[12px] font-medium text-[#1F2937] truncate">
            {(sessionUser?.role || 'Platform operator').split(',').map((r) => r.trim()).filter(Boolean).join(' · ')}
          </span>
        </div>
        {/* Which devices can act as this operator is an IDENTITY question, so it lives in
            the operator's own menu — not behind a Utilities tab labelled as POS hardware. */}
        {sessionUser ? <BiometricPanel /> : null}
      </TopBarPopover>
    </header>
  );
};

/* TopBarPopover — reusable button that opens a styled popover panel */

/**
 * ONE OPEN AT A TIME, across every popover in the bar.
 *
 * Each of these owned its `open` independently, and the document-click listener that closes them
 * could never fire for a sibling: the wrapper calls `stopPropagation`, so clicking Notifications
 * while Alerts was open never reached Alerts' listener. Two 360px panels then rendered on top of
 * each other, a few pixels apart.
 *
 * A module-level registry rather than lifted state, because these are separate elements in
 * separate parts of the header with no common owner short of TopBar itself — and the rule
 * ("opening one closes the others") belongs to the component, not to each call site that would
 * otherwise have to remember to wire an id.
 */
const openPopovers = new Set();

/**
 * `header` and `footer` exist because the `items` API can only express ONE shape: an icon tile, a
 * label, a sub-line, a meta. That is right for a list of alerts and wrong for a profile menu, where
 * "Role" is a FACT and "Sign out" is a destructive ACT — rendering both as the same row made them
 * look equally clickable, and left the biometric block (which starts at px-4) misaligned against a
 * 40px icon rail it does not have.
 */
export const TopBarPopover = ({ icon, trigger, title, subtitle, header, footer, items = [], empty, dot, badge, align = 'right', onViewAll, viewAllLabel = 'View all', children }) => {
  const [open, setOpen] = useState(false);
  // Registered while open so a sibling can close this one directly.
  useEffect(() => {
    if (!open) return undefined;
    openPopovers.add(setOpen);
    const close = () => setOpen(false);
    const t = setTimeout(() => document.addEventListener('click', close), 0);
    return () => { openPopovers.delete(setOpen); clearTimeout(t); document.removeEventListener('click', close); };
  }, [open]);
  const toggle = () => {
    // Close every sibling BEFORE opening, so the bar can never show two panels at once.
    if (!open) openPopovers.forEach((closeOther) => { if (closeOther !== setOpen) closeOther(false); });
    setOpen((o) => !o);
  };
  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      {trigger ? (
        // A FUNCTION TRIGGER GETS THE OPEN STATE. Passing a finished element gave the component no
        // way to reach inside it, so a custom trigger could never carry `aria-expanded` — the
        // icon-button branch below announced itself to a screen reader and the profile and backend
        // controls did not. `trigger={({ open }) => …}` closes that; a plain element still works,
        // and is still the right thing when the trigger has no state to reflect.
        <span onClick={toggle}>{typeof trigger === 'function' ? trigger({ open }) : trigger}</span>
      ) : (
        <button onClick={toggle} className="w-9 h-9 rounded-md hover:bg-[#F2F1ED] flex items-center justify-center relative" aria-expanded={open}>
          {icon}
          {/* A COUNT WHERE THERE IS ONE, a dot only where there is nothing to count.
              The bare dot said "something is wrong" and stopped there — an operator could not tell
              one stuck payout from nine without opening the panel. Sits on the icon's top-right
              corner with a white ring so it reads as a badge ON the icon rather than beside it. */}
          {badge > 0 ? (
            <span className={`absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-[3px] rounded-full ring-2 ring-white text-[9.5px] font-semibold leading-none flex items-center justify-center tabular-nums ${
              dot === 'red' ? 'bg-[#C53030] text-white' : dot === 'amber' ? 'bg-[#C48319] text-white' : 'bg-[#2F8B4F] text-white'
            }`}>{badge > 9 ? '9+' : badge}</span>
          ) : dot ? (
            <span className={`absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full ${dot === 'red' ? 'bg-[#C53030]' : dot === 'amber' ? 'bg-[#C48319]' : 'bg-[#2F8B4F]'}`} />
          ) : null}
        </button>
      )}
      {open && (
        <div className={`absolute top-full mt-2 ${align === 'right' ? 'right-0' : 'left-0'} bg-white border border-[#E6E4E1] rounded-[10px] shadow-xl z-50 w-[360px] overflow-hidden`}>
          {header || ((title || subtitle) && (
            <div className="px-4 py-3 border-b border-[#EEEDEA]">
              {title && <div className="text-[13px] font-semibold text-[#1F2937]">{title}</div>}
              {subtitle && <div className="text-[11px] text-[#6B7280] mt-0.5">{subtitle}</div>}
            </div>
          ))}
          <div className="max-h-[420px] overflow-y-auto">
            {/* A rich section (device list, inline form…) above the flat item list — the
                items API can only express a row that navigates or toasts. */}
            {children}
            {items.length === 0 && empty && (
              <div className="px-4 py-6 text-center text-[12px] text-[#6B7280]">{empty}</div>
            )}
            {items.map((it, i) => {
              if (it.divider) return <div key={i} className="h-px bg-[#EEEDEA] my-1" />;
              const Icon = it.icon;
              return (
                <button key={i} onClick={() => { setOpen(false); it.onClick ? it.onClick() : toast(it.label, 'info', it.sub); }} className={`w-full text-left px-4 py-2.5 flex items-start gap-3 hover:bg-[#FBFAF7] ${it.tone === 'danger' ? 'text-[#9B1F1F]' : 'text-[#1F2937]'}`}>
                  {Icon && (
                    <div className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${it.tone === 'red' ? 'bg-[#FEE2E2] text-[#9B1F1F]' : it.tone === 'amber' ? 'bg-[#FEF3C7] text-[#8A5A12]' : it.tone === 'danger' ? 'bg-[#FEE2E2] text-[#9B1F1F]' : 'bg-[#F2F1ED] text-[#4B5563]'}`}>
                      <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-[12.5px] font-medium leading-tight">{it.label}</div>
                    {it.sub && <div className={`text-[10.5px] mt-0.5 ${it.tone === 'danger' ? 'text-[#9B1F1F]/70' : 'text-[#6B7280]'}`}>{it.sub}</div>}
                  </div>
                  {it.meta && <div className="text-[10.5px] text-[#9CA3AF] tabular-nums flex-shrink-0 mt-0.5">{it.meta}</div>}
                </button>
              );
            })}
          </div>
          {footer}
          {/* Only when there is somewhere to go. The footer used to render for ANY populated
              popover, so Backend and the profile menu both offered "View all" and it did nothing
              but close the panel — a control whose only function was to look like one. */}
          {onViewAll && items.length > 0 && !items[0].divider && (
            <div className="px-4 py-2.5 border-t border-[#EEEDEA] bg-[#FBFAF7] text-center">
              <button onClick={() => { setOpen(false); onViewAll(); }} className="text-[11.5px] text-[#1F2937] hover:underline">{viewAllLabel}</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ============================================================================
   FILTER BAR
   ============================================================================ */
